<template>
  <div class="cl-wrap">
    <div class="container">

      <div class="header">
        <div class="version-badge">Changelog</div>
        <h1>HypnoMaker</h1>
        <div class="subtitle">What's New &nbsp;·&nbsp; v1.5</div>
      </div>

      <div class="divider"><span class="divider-glyph">v1.5</span></div>

      <!-- v1.5: AUDIO -->
      <div class="section">
        <div class="section-title">Audio</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ Source Audio Mixing</div>
          <p>Pull the audio off your video clips and mix it into the output, with an adjustment bar to balance it against the rest of the mix. In three-panel mode you can even choose which panel's audio gets mixed in.</p>
          <ul>
            <li><strong>Diegetic layering</strong>: use a clip's own audio as texture under the script.</li>
            <li><strong>Per-panel selection</strong>: pick which panel drives the sound in three-panel mode.</li>
          </ul>
        </div>

        <div class="feature">
          <div class="feature-name">Fish Audio BYO Key</div>
          <p>Bring your own Fish Audio key for custom scripts, just like the ElevenLabs BYO key. Fish Audio is substantially cheaper, so heavy custom generation costs a lot less.</p>
        </div>

        <div class="feature">
          <div class="feature-name">HypnoMode Audio Fix</div>
          <p>The little hiccups between audio clips in Hypno Mode are gone. Transitions between segments are now smooth as butter.</p>
        </div>
      </div>

      <!-- v1.5: WORKFLOW -->
      <div class="section">
        <div class="section-title">Workflow</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ Pick &amp; Order Your Files</div>
          <p>You're no longer stuck pointing HypnoMaker at a folder and taking whatever it grabs. A new dialog lets you select individual files and set their exact order. If you've ever wanted a specific clip in a specific place, this gets you there.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Captions &amp; Script Browsing</div>
          <p>Captions and the prebuilt script library can now be filtered by category: pick "Affirmations" and see just those. With 102 scripts in the library, this stopped being a nicety and became a necessity. Plus UI cleanup on the custom-caption flow and BYO key dialogs.</p>
        </div>
      </div>

      <!-- v1.5: VIDEO -->
      <div class="section">
        <div class="section-title">Video</div>

        <div class="feature">
          <div class="feature-name">Three-Panel Mode Upgrades</div>
          <p>Triptych mode is now called three-panel mode (same idea, clearer name), and it got meaningfully more capable.</p>
          <ul>
            <li><strong>Three different panels</strong>: no longer limited to the mirrored inside/outside layout.</li>
            <li><strong>Cut-sync behaviors (PMV)</strong>: synchronized, alternating, or independent cuts per panel. Independent gets pleasantly disorienting.</li>
            <li><strong>Per-panel source audio</strong>: choose which panel's audio feeds the mix.</li>
          </ul>
        </div>

        <div class="feature">
          <div class="feature-name">PMV Improvements</div>
          <p>Sync is improved across the board. Transitions can now be assigned per video, randomly or in a set order, instead of one transition applied globally. And the progress bar now reflects actual progress instead of lying to you.</p>
        </div>
      </div>

      <!-- v1.5: SCRIPTS -->
      <div class="section">
        <div class="section-title">Scripts</div>

        <div class="feature">
          <div class="feature-name">Ten New Scripts: Library at 102</div>
          <p>Bambi Chastity Encouragement, Cock Worship Trance, Cuckold Encouragement, Cum Eater Conditioning, Limp and Small, Sissy Pussy Training, Alpha Worship, Caged Denial, Chastity Cuckold Cleanup, and Small Penis Chastity Acceptance.</p>
          <p>That brings the premade library to <strong>102 scripts</strong>, every one shipping with prerendered voiceovers in both the ElevenLabs female voices and the Fish Audio male voices. The White Script and the rest of the grounding material are all still in there. The ascent and the decompression both ship.</p>
        </div>
      </div>

      <div class="divider"><span class="divider-glyph">v1.4</span></div>

      <!-- v1.4: VIDEO -->
      <div class="section">
        <div class="section-title">Video</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ Triptych Mode</div>
          <p>Both Hypno and PMV mode now support triptych output: three-panel layered video. Lots of room to play with visual layering, mirrored panels, and rhythmic asymmetry.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Beat Sync: Finally Working (PMV)</div>
          <p>Beat sync for PMV mode is <em>finally</em> working after a lot of trial and error. Detection is pretty damn good on most source tracks. If you generate something good with it, post it!</p>
        </div>

        <div class="feature">
          <div class="feature-name">Video-to-Video Transitions</div>
          <p>Transitions now go video-to-video instead of cutting through black. A small change with a big perceptual impact: the output feels more continuous and less amateurish.</p>
        </div>
      </div>

      <!-- v1.4: AUDIO -->
      <div class="section">
        <div class="section-title">Audio</div>

        <div class="feature">
          <div class="feature-name">Male Voice via Fish Audio</div>
          <p>All scripts now support a male voice option through Fish Audio, alongside the existing ElevenLabs voices. Tested across the full script library, it holds up across registers, from the harsh ones to the meditative ones.</p>
        </div>

        <div class="feature">
          <div class="feature-name">BYO Key Error Handling</div>
          <p>The BYO ElevenLabs key flow was failing silently on bad keys in some edge cases. Errors now surface clearly, so you know whether the issue is your key, your quota, or the API.</p>
        </div>
      </div>

      <!-- v1.4: SCRIPTS & MISC -->
      <div class="section">
        <div class="section-title">Scripts &amp; More</div>

        <div class="feature">
          <div class="feature-name">Six New Scripts</div>
          <p>Plapping, Estrogen Encouragement, Cathedral (a sacral-mythic script), Sissygasm, Permanent Chastity, and the White Script: a decompression / grounding script with no eroticism, no kink language, no scene framing. It clears triggers, returns the body to baseline, and gives explicit permission to keep what serves you and let go of what doesn't. HypnoMaker ships the off-ramp, not just the ascent.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Configurable Paths</div>
          <p>Set both the video download path and the cache location yourself. Overdue for anyone on custom storage layouts or external drives.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Smaller Things</div>
          <p>The PMV update bar now actually updates, plus several minor UI fixes that make the whole thing feel a little less janky.</p>
        </div>
      </div>

      <div class="divider"><span class="divider-glyph">v1.3</span></div>

      <!-- v1.3: SCRIPTS -->
      <div class="section">
        <div class="section-title">Scripts</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ 19 New Scripts</div>
          <p>A big content drop across a wide spread: being worshipped, beta acceptance, breeding fantasy, CEI countdown, cuckold humiliation, cuckqueen, cum worship, edging marathon, femdom forced bi (two of them), femdom instructions, findom indoctrination, keeping holes desperate, little space, multiple orgasm conditioning, kitten play, pup play, vixen POV, and whore transformation.</p>
        </div>
      </div>

      <!-- v1.3: AUDIO -->
      <div class="section">
        <div class="section-title">Audio &amp; Voice</div>

        <div class="feature">
          <div class="feature-name">Bring Your Own ElevenLabs Key</div>
          <p>Plug in your own ElevenLabs API key, write whatever custom script you want, and pick any voice in their library. One of the most requested features. You're not locked into the built-in scripts anymore.</p>
        </div>
      </div>

      <!-- v1.3: QUALITY OF LIFE -->
      <div class="section">
        <div class="section-title">Quality of Life</div>

        <div class="feature">
          <div class="feature-name">Script &amp; Media Info</div>
          <p>Before you hit create, HypnoMaker shows how long a script runs and counts your selected videos and images. Less guesswork, fewer surprised-Pikachu moments when a 3-minute media folder meets a 20-minute script.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Customizable Caption Color</div>
          <p>Pick your text color. Small thing, big difference for matching the vibe you're going for.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Granular Progress Bar</div>
          <p>The progress bar now shows what stage of processing your video is at.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Way Smaller Install</div>
          <p>The package is down to around 550 MB. TTS audio is pulled from a server on first use and cached locally, so you're not downloading a giant blob of voice files you might never use.</p>
        </div>
      </div>

      <div class="divider"><span class="divider-glyph">v1.2</span></div>

      <!-- v1.2: VISUALS -->
      <div class="section">
        <div class="section-title">Visuals</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ Transition Library</div>
          <p>Ten transition styles control how clips flow into each other. The video keeps playing throughout, never freezing on a frame.</p>
          <ul>
            <li><strong>Fade, Spiral, Pulse Zoom, Radial Wipe, Breathe, Tunnel, Dissolve</strong>, plus Luma Wipe and its inverse.</li>
            <li>Transitions fire only at real clip boundaries, not caption changes.</li>
          </ul>
        </div>

        <div class="feature">
          <div class="feature-name">Expanded Video Filters</div>
          <p>Twelve filters including Psychedelic strobing, Chromatic Aberration, Warm/Cool grades, Vignette, and B&amp;W High Contrast, or roll Random per clip.</p>
        </div>
      </div>

      <!-- v1.2: OUTPUT -->
      <div class="section">
        <div class="section-title">Output</div>

        <div class="feature">
          <div class="feature-name">Vertical Resolutions</div>
          <p>New portrait presets, Vertical 720p and Vertical 1080p, for mobile-first content.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Text Supersampling</div>
          <p>Captions can render at up to 200% resolution before scaling down, for noticeably sharper text edges.</p>
        </div>
      </div>

      <div class="divider"><span class="divider-glyph">v1.1</span></div>

      <!-- PMV MODE -->
      <div class="section">
        <div class="section-title">New Premium Feature</div>

        <div class="feature feature-hero">
          <div class="feature-name">✦ PMV Mode</div>
          <p>Build your own Porn Music Videos directly inside HypnoMaker. Drop in your media, pick your music, and let the tool handle the cutting. PMV Mode is a fully realized editing pipeline, no external software needed.</p>
          <p class="hero-note">Available exclusively to premium users.</p>
          <ul>
            <li><strong>Beat-synced cuts</strong>: enable <em>Sync media cuts to beats</em> in the Music tab; librosa detects your track's rhythm and cuts on it automatically. Falls back to time-based cuts if librosa isn't installed.</li>
            <li><strong>Beats Per Cut</strong>: control the pace by switching every 1, 2, 4, or 8 beats. Frantic or slow-burn, your call.</li>
            <li>Beat sync applies to both image slideshows and video clips.</li>
          </ul>
        </div>
      </div>

      <!-- SCRIPTS -->
      <div class="section">
        <div class="section-title">Scripts</div>

        <div class="feature">
          <div class="feature-name">30+ New Hypno Scripts</div>
          <p>Over thirty new scripts added to the built-in library, each containing 150–250 lines of hypnotic content.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Script Preview</div>
          <p>Preview scripts before committing, and see exactly how they'll render before your video does.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Write Your Own Scripts</div>
          <p>Create your own hypno scripts directly inside the program with a new built-in editor. Write, save, and use your scripts without ever leaving HypnoMaker.</p>
        </div>
      </div>

      <!-- AUDIO / TTS -->
      <div class="section">
        <div class="section-title">Audio &amp; Voice</div>

        <div class="feature">
          <div class="feature-name">New Realistic TTS</div>
          <p>Voice generation rebuilt using ElevenLabs for significantly more natural, human-sounding output.</p>
        </div>
      </div>

      <!-- VISUALS -->
      <div class="section">
        <div class="section-title">Visuals</div>

        <div class="feature">
          <div class="feature-name">New Overlays</div>
          <p>Additional overlay options added for more variety in your output.</p>
        </div>

        <div class="feature">
          <div class="feature-name">Video Filters</div>
          <p>New option to apply filters to your video for custom visual treatment.</p>
        </div>
      </div>

      <!-- UI -->
      <div class="section">
        <div class="section-title">Interface</div>

        <div class="feature">
          <div class="feature-name">Redesigned Options UI</div>
          <p>The options panel has been fully reorganized for clarity. Everything is easier to find and faster to configure.</p>
        </div>
      </div>

      <!-- FIXES -->
      <div class="section">
        <div class="section-title">Bug Fixes</div>
        <ul class="fix-list">
          <li>GIFs no longer crash the program</li>
        </ul>
      </div>

      <div class="cl-footer">
        Thank you for your support. <strong>HypnoMaker</strong> keeps getting deeper.<br>
        <a href="https://hypnomaker.itch.io/hypnomaker" target="_blank" rel="noopener" style="color:#ff3d82;font-style:normal;font-weight:700;text-decoration:underline;">Download on itch.io →</a>
      </div>

    </div>
  </div>
</template>

<script setup>
// static page
</script>

<style scoped>
.cl-wrap {
  min-height: 100vh;
  padding-top: 100px;
}

.container {
  max-width: 720px;
  margin: 0 auto;
  padding: 20px 32px 80px;
}

/* Header */
.header {
  text-align: center;
  margin-bottom: 52px;
}

.version-badge {
  display: inline-block;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #ff3d82;
  border: 1.5px solid #ff85b0;
  border-radius: 20px;
  padding: 4px 18px;
  margin-bottom: 18px;
  background: #fff0f5;
}

h1 {
  font-family: 'Pacifico', cursive;
  font-size: clamp(32px, 6vw, 50px);
  font-weight: 400;
  color: #ff3d82;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
  text-shadow: 2px 3px 0px rgba(255, 100, 150, 0.14);
}

.subtitle {
  color: #b06080;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.08em;
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 36px 0 28px;
}
.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1.5px;
}
.divider::before { background: linear-gradient(to right, transparent, #ffd6e7); }
.divider::after  { background: linear-gradient(to left, transparent, #ffd6e7); }
.divider-glyph { color: #ff85b0; font-size: 16px; }

/* Section */
.section { margin-bottom: 38px; }

.feature, .fix-list { text-align: left; }

.section-title {
  font-family: 'Pacifico', cursive;
  font-size: 15px;
  font-weight: 400;
  color: #ff3d82;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.section-title::after {
  content: '';
  flex: 1;
  height: 1.5px;
  background: #ffd6e7;
}

/* Feature cards */
.feature {
  background: #fff5f8;
  border: 1.5px solid #ffd6e7;
  border-left: 4px solid #ff85b0;
  border-radius: 10px;
  padding: 18px 22px;
  margin-bottom: 12px;
  transition: border-left-color 0.25s, box-shadow 0.25s;
}
.feature:hover {
  border-left-color: #ff3d82;
  box-shadow: 0 4px 18px rgba(255, 80, 140, 0.09);
}

.feature-name {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #ff3d82;
  margin-bottom: 6px;
}

.feature p {
  color: #3d1a26;
  font-size: 16px;
  line-height: 1.65;
}

.feature ul {
  list-style: none;
  margin-top: 10px;
  padding: 0;
}
.feature ul li {
  color: #b06080;
  font-size: 15px;
  padding: 3px 0 3px 20px;
  position: relative;
}
.feature ul li::before {
  content: '✦';
  position: absolute;
  left: 0;
  color: #ff85b0;
  font-size: 11px;
  top: 5px;
}
.feature ul li strong { color: #e0306e; }

.feature-hero {
  border-left: 4px solid #ff3d82;
  background: linear-gradient(135deg, #fff5f8 0%, #ffe8f2 100%);
  box-shadow: 0 6px 24px rgba(255, 80, 140, 0.10);
}
.feature-hero .feature-name { font-size: 14px; letter-spacing: 0.10em; }

.hero-note {
  margin-top: 10px;
  color: #b06080 !important;
  font-style: italic;
  font-size: 15px !important;
}

/* Fix list */
.fix-list {
  list-style: none;
  padding: 0;
  background: #fff5f8;
  border: 1.5px solid #ffd6e7;
  border-radius: 10px;
  padding: 4px 22px;
}
.fix-list li {
  padding: 8px 0 8px 24px;
  border-bottom: 1px solid #ffd6e7;
  position: relative;
  font-size: 16px;
  color: #3d1a26;
}
.fix-list li:last-child { border-bottom: none; }
.fix-list li::before {
  content: '✦';
  position: absolute;
  left: 0;
  color: #ff85b0;
  font-size: 11px;
  top: 11px;
}

/* Footer */
.cl-footer {
  text-align: center;
  margin-top: 56px;
  padding-top: 28px;
  border-top: 1.5px solid #ffd6e7;
  color: #b06080;
  font-size: 15px;
  font-style: italic;
}
.cl-footer strong {
  color: #ff3d82;
  font-style: normal;
  font-weight: 700;
}

@media (max-width: 600px) {
  .container {
    padding: 16px 16px 60px;
  }
  .feature {
    padding: 14px 16px;
  }
  .fix-list {
    padding: 4px 16px;
  }
  .header {
    margin-bottom: 24px;
  }
  .divider {
    margin: 16px 0 16px;
  }
}
</style>
